/**
 * Sequential per-domain reading paths — the "spine" layered over the browsable
 * article library: ordered articles per domain with read state, a resume
 * pointer, completion %, and derived reading-time estimates.
 *
 * DERIVE-ONLY metadata: minutes come from the bundled `word_count`, ordering is
 * bundle array order within a domain (same DRIFT NOTE semantics as
 * `filterToFreeSet` — a content reorder rotates the path; accepted v1).
 *
 * Pure and platform-agnostic: callers pass already-fetched plain objects
 * (bundled ARTICLES, APP_DOMAIN_LIST, the user's read map and
 * `articles_progress` entries). No SDKs, no UI.
 */

/**
 * word_count → whole minutes at a reading speed. Returns null (not a fake
 * "1 min") when word_count is missing/zero so apps can hide the chip.
 *
 * @param {number|null|undefined} wordCount
 * @param {number} [wpm=200]
 * @returns {number|null}
 */
export function estimateReadingMinutes(wordCount, wpm = 200) {
  const words = Number(wordCount);
  if (!Number.isFinite(words) || words <= 0 || !Number.isFinite(wpm) || wpm <= 0) return null;
  return Math.max(1, Math.ceil(words / wpm));
}

/**
 * Compact remaining-time label from whole minutes: "~35m left" / "~2h left"
 * (hours rounded). '' when unknown/zero so apps can hide the text. Shared so
 * web and mobile can't drift on the format.
 *
 * @param {number|null|undefined} minutes
 * @returns {string}
 */
export function formatMinutesLeft(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  if (minutes >= 60) return `~${Math.round(minutes / 60)}h left`;
  return `~${minutes}m left`;
}

/** Type-insensitive read-map lookup (object keys are strings, ids arrive as either). */
function isRead(readMap, id) {
  return id != null && Boolean(readMap?.[String(id)]);
}

/**
 * Resolve one progress entry to an article id within an ordered domain path.
 *
 * `last_seen_article_id` (written by both platforms) is AUTHORITATIVE when
 * present: a match resolves it, a miss returns null. A miss means the entry
 * belongs to a DIFFERENT domain (or the article left the bundle) — since
 * `buildReadingPaths` evaluates every entry against every domain, indexing this
 * domain by a foreign `last_index` would surface an arbitrary article in a
 * domain the user never opened. `last_index` is therefore only a fallback for
 * entries that recorded no id at all (never the case for current writers, but
 * kept for legacy/robustness); it is NOT a cross-domain positional pointer.
 *
 * @param {Object|null} progressEntry { last_seen_article_id?, last_index? }
 * @param {Array<string|number>} orderedArticleIds the domain path, in order
 * @returns {string|number|null}
 */
export function resolveResumeArticleId(progressEntry, orderedArticleIds) {
  if (!progressEntry || !Array.isArray(orderedArticleIds)) return null;
  const seenId = progressEntry.last_seen_article_id;
  if (seenId != null) {
    const match = orderedArticleIds.find((id) => String(id) === String(seenId));
    return match != null ? match : null;
  }
  const idx = progressEntry.last_index;
  if (Number.isInteger(idx) && idx >= 0 && idx < orderedArticleIds.length) {
    return orderedArticleIds[idx];
  }
  return null;
}

/**
 * Build the hybrid spine: one ordered reading path per domain.
 *
 * Domains appear in `domainList` order; articles keep bundle order within their
 * domain. An article whose `topic_id` matches no domain row is excluded (parity
 * with `filterToFreeSet` — no domain row means no door to open it under).
 *
 * Resume pointers: each `articles_progress` entry is resolved by ARTICLE
 * identity (not set id, which varies by surface) to the domain containing that
 * article. When several entries land in one domain the LAST in array order wins
 * (entries are appended per set; web entries carry no timestamp — deterministic).
 *
 * @param {Object} params
 * @param {Array}  params.articles   bundled ARTICLES [{ id, title, article_title, topic_id, word_count }]
 * @param {Array}  params.domainList APP_DOMAIN_LIST rows [{ id, domain_name }]
 * @param {Object} params.readMap    { [articleId]: truthy }
 * @param {Array}  params.articlesProgress profile.articles_progress
 * @returns {Array<{ domainId, domainName, total, readCount, completionPct,
 *                   totalMinutes, remainingMinutes, resumeArticleId,
 *                   nextArticleId, items: Array }>}
 *          items: [{ id, title, minutes, read, index }]
 */
export function buildReadingPaths({ articles, domainList, readMap, articlesProgress }) {
  const domains = domainList ?? [];
  const all = articles ?? [];

  return domains.map((domain) => {
    const domainArticles = all.filter((a) => String(a?.topic_id) === String(domain?.id));
    const orderedIds = domainArticles.map((a) => a.id);

    const items = domainArticles.map((a, index) => ({
      id: a.id,
      title: a.article_title ?? a.title ?? '',
      minutes: estimateReadingMinutes(a.word_count),
      read: isRead(readMap, a.id),
      index,
    }));

    const readCount = items.filter((i) => i.read).length;
    const total = items.length;
    const totalMinutes = items.reduce((sum, i) => sum + (i.minutes ?? 0), 0);
    const remainingMinutes = items.reduce((sum, i) => sum + (i.read ? 0 : i.minutes ?? 0), 0);

    // Last progress entry (array order) resolving into this domain wins.
    let resumeArticleId = null;
    for (const entry of articlesProgress ?? []) {
      const resolved = resolveResumeArticleId(entry, orderedIds);
      if (resolved != null) resumeArticleId = resolved;
    }

    const nextItem = items.find((i) => !i.read) ?? null;

    return {
      domainId: domain?.id,
      domainName: domain?.domain_name ?? '',
      total,
      readCount,
      completionPct: total > 0 ? Math.round((100 * readCount) / total) : 0,
      totalMinutes,
      remainingMinutes,
      resumeArticleId,
      nextArticleId: nextItem ? nextItem.id : null,
      items,
    };
  });
}

/**
 * The openable pager list + index for a domain article — the one openability
 * definition shared by the articles landing and the Home nudge (apps inject
 * the brand flag; navigation stays app-side).
 *
 * Semantics:
 *  - metering or premium → the FULL domain path (locked pages render as unlock
 *    interstitials in the pager, never auto-spend);
 *  - legacy free model → `slice(0, num_articles)`, pinned to filterToFreeSet's
 *    By-Topic semantics: cap undefined/null = uncapped (full domain),
 *    cap 0 = nothing free;
 *  - bundle order, no id sort — matches buildReadingPaths numbering;
 *  - null when the article isn't in the openable list.
 *
 * @param {Object} params
 * @param {Array}   params.articles   bundled ARTICLES
 * @param {Array}   params.domainList APP_DOMAIN_LIST rows
 * @param {string|number} params.domainId
 * @param {string|number} params.articleId
 * @param {boolean} params.isSubscribe
 * @param {boolean} params.meteringEnabled brand METERED_ARTICLE_UNLOCKS != null
 * @returns {{ openList: Array, index: number, domainRow: Object|undefined } | null}
 */
export function resolveArticleOpenTarget({ articles, domainList, domainId, articleId, isSubscribe, meteringEnabled }) {
  const domainRow = (domainList ?? []).find((d) => String(d?.id) === String(domainId));
  let openList = (articles ?? []).filter((a) => String(a?.topic_id) === String(domainId));
  if (!isSubscribe && !meteringEnabled) {
    openList = openList.slice(0, domainRow?.num_articles);
  }
  const index = openList.findIndex((a) => String(a?.id) === String(articleId));
  if (index < 0) return null;
  return { openList, index, domainRow };
}

/**
 * Whole-library rollup for the landing stats and the Home nudge.
 *
 * `resume` is the single continue-reading target: the first domain (domainList
 * order) whose resume pointer lands on a still-UNREAD article — a fully-read
 * domain's stale pointer never surfaces.
 *
 * @param {Array} paths from buildReadingPaths
 * @returns {{ totalArticles, totalRead, completionPct, totalMinutes, remainingMinutes,
 *             resume: { domainId, domainName, articleId, articleTitle,
 *                       articleIndex, domainTotal } | null }}
 */
export function buildLibrarySummary(paths) {
  const rows = paths ?? [];
  const totalArticles = rows.reduce((s, p) => s + p.total, 0);
  const totalRead = rows.reduce((s, p) => s + p.readCount, 0);
  const totalMinutes = rows.reduce((s, p) => s + p.totalMinutes, 0);
  const remainingMinutes = rows.reduce((s, p) => s + p.remainingMinutes, 0);

  let resume = null;
  for (const path of rows) {
    if (path.resumeArticleId == null) continue;
    const item = path.items.find((i) => String(i.id) === String(path.resumeArticleId));
    if (!item || item.read) continue;
    resume = {
      domainId: path.domainId,
      domainName: path.domainName,
      articleId: item.id,
      articleTitle: item.title,
      articleIndex: item.index,
      domainTotal: path.total,
    };
    break;
  }

  return {
    totalArticles,
    totalRead,
    completionPct: totalArticles > 0 ? Math.round((100 * totalRead) / totalArticles) : 0,
    totalMinutes,
    remainingMinutes,
    resume,
  };
}
